import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * SopaInteractiva — capa jugable que se superpone sobre la página del PDF
 * donde está impresa la sopa de letras.
 *
 * La cuadrícula es exactamente la misma que se imprimió: el backend la genera
 * usando el id de la edición como semilla, así que lo que el lector traza en
 * pantalla coincide celda a celda con lo que tiene en el papel.
 *
 * Solo acepta trazos rectos — horizontal, vertical o diagonal exacta — y
 * valida contra la posición real de cada palabra, en los dos sentidos.
 */
export default function SopaInteractiva({ datos, onCerrar }) {
  const cols = datos?.cols || 0;
  const grid = useMemo(() => datos?.grid || [], [datos]);
  const palabras = useMemo(
    () => Object.keys(datos?.palabras || {}).sort(),
    [datos]
  );

  // Ruta de celdas de cada palabra, precalculada para comparar con el trazo
  const rutas = useMemo(() => {
    const r = {};
    for (const [p, d] of Object.entries(datos?.palabras || {})) {
      r[p] = Array.from({ length: d.largo }, (_, i) =>
        `${d.fila + d.dy * i},${d.col + d.dx * i}`).join('|');
    }
    return r;
  }, [datos]);

  const [halladas, setHalladas] = useState(() => new Set());
  const [seleccion, setSeleccion] = useState([]);
  const [mensaje, setMensaje] = useState(null);
  const trazando = useRef(false);
  const inicio = useRef(null);

  useEffect(() => {
    if (!mensaje) return;
    const t = setTimeout(() => setMensaje(null), 1600);
    return () => clearTimeout(t);
  }, [mensaje]);

  const celdaDesdeEvento = (ev) => {
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    if (!el || !el.dataset || el.dataset.f === undefined) return null;
    return { f: +el.dataset.f, c: +el.dataset.c };
  };

  // Solo trazos rectos: horizontal, vertical o diagonal exacta
  const camino = (a, b) => {
    const df = b.f - a.f, dc = b.c - a.c;
    if (df !== 0 && dc !== 0 && Math.abs(df) !== Math.abs(dc)) return null;
    const n = Math.max(Math.abs(df), Math.abs(dc));
    const pf = n === 0 ? 0 : df / n, pc = n === 0 ? 0 : dc / n;
    return Array.from({ length: n + 1 }, (_, i) => `${a.f + pf * i},${a.c + pc * i}`);
  };

  const soltar = useCallback(() => {
    if (!trazando.current) return;
    trazando.current = false;
    setSeleccion(sel => {
      if (sel.length > 1) {
        const directo = sel.join('|');
        const inverso = sel.slice().reverse().join('|');
        const hallada = Object.keys(rutas).find(
          p => !halladas.has(p) && (rutas[p] === directo || rutas[p] === inverso)
        );
        if (hallada) {
          setHalladas(prev => new Set(prev).add(hallada));
          setMensaje(`${hallada} ✓`);
        } else {
          setMensaje('Esas letras no forman una palabra de la lista');
        }
      }
      return [];
    });
  }, [rutas, halladas]);

  useEffect(() => {
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
  }, [soltar]);

  if (cols === 0 || grid.length === 0) return null;

  const celdasHalladas = new Set();
  for (const p of halladas) {
    if (rutas[p]) rutas[p].split('|').forEach(k => celdasHalladas.add(k));
  }

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 10,
      background: 'rgba(12,12,12,0.93)', padding: 16, zIndex: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff' }}>
        <strong style={{ fontSize: 15, letterSpacing: '.04em' }}>SOPA DE LETRAS</strong>
        <span style={{ fontSize: 12, opacity: .7 }}>
          {halladas.size} de {palabras.length}
        </span>
        <button
          onClick={onCerrar}
          style={{
            marginLeft: 8, fontSize: 12, padding: '4px 10px', borderRadius: 14,
            border: '1px solid rgba(255,255,255,.3)', background: 'transparent',
            color: '#fff', cursor: 'pointer',
          }}
        >Ver la página</button>
      </div>

      <table
        style={{
          borderCollapse: 'collapse', touchAction: 'none', userSelect: 'none',
          border: '2px solid #c9a227', background: '#fff',
        }}
        onPointerDown={(ev) => {
          const cel = celdaDesdeEvento(ev);
          if (!cel) return;
          ev.preventDefault();
          trazando.current = true;
          inicio.current = cel;
          setSeleccion([`${cel.f},${cel.c}`]);
        }}
        onPointerMove={(ev) => {
          if (!trazando.current) return;
          const cel = celdaDesdeEvento(ev);
          if (!cel) return;
          const ruta = camino(inicio.current, cel);
          if (ruta) setSeleccion(ruta);
        }}
      >
        <tbody>
          {grid.map((fila, f) => (
            <tr key={f}>
              {[...fila].map((ch, c) => {
                const clave = `${f},${c}`;
                const sel = seleccion.includes(clave);
                const ok = celdasHalladas.has(clave);
                return (
                  <td
                    key={c}
                    data-f={f}
                    data-c={c}
                    style={{
                      width: 'min(3.2vw, 26px)', height: 'min(3.2vw, 26px)',
                      textAlign: 'center', fontFamily: 'ui-monospace, monospace',
                      fontWeight: 700, fontSize: 'min(1.9vw, 14px)', cursor: 'pointer',
                      color: ok && !sel ? '#166534' : '#1a1a1a',
                      background: sel ? '#c9a227'
                                : ok ? '#dcfce7'
                                : (f + c) % 2 === 0 ? '#faf7f0' : '#fff',
                      borderRadius: sel || ok ? '50%' : 0,
                    }}
                  >{ch}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px 14px', justifyContent: 'center',
        maxWidth: 760, fontSize: 12,
      }}>
        {palabras.map(p => (
          <span key={p} style={{
            color: halladas.has(p) ? 'rgba(255,255,255,.45)' : '#fff',
            textDecoration: halladas.has(p) ? 'line-through' : 'none',
          }}>{p}</span>
        ))}
      </div>

      <div style={{ height: 20, fontSize: 12, color: '#c9a227' }}>{mensaje}</div>
    </div>
  );
}
